<script lang="ts" setup>
import { PropType, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { useStore } from 'vuex';
import {
  Invoice,
  InvoiceLine,
  InvoiceOption,
  invoiceValidations,
} from '@/models/invoice';
import { Option } from '../../models/option';
import Field from '@/components/common/Field.vue';
import FieldNumber from '@/components/common/FieldNumber.vue';
import FieldOptions from '@/components/common/FieldOptions.vue';
import InvoiceFormLine from './InvoiceFormLine.vue';
import InvoiceFormOption from './InvoiceFormOption.vue';
import Loading from '@/components/common/Loading.vue';
import { useLoading } from '../common/loading.composable';
import { BalanceActions } from '@/stores/balance.store';
import { getMonthName } from '@/core/util/date.util';
import { InvoiceInfo } from '@/services/balance.service';
import { DataResponse } from '@/core/services/base.service';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { loading, setLoading } = useLoading();

const props = defineProps({
  data: {
    type: Object as PropType<{ invoice: Invoice; info: InvoiceInfo }>,
    required: true,
  },
});
const emit = defineEmits(['close']);

const date = new Date(route.params.date as string);
const invoice = reactive<Invoice>(
  new Invoice({
    ...props.data.invoice,
    project_id: props.data.info.project.id,
    company_id: props.data.info.company.id,
  }),
);
const $v = useVuelidate(invoiceValidations, invoice);
const language = ref<string>('en');
const languages = ref<Option<string>[]>([
  {
    name: 'Nederlands',
    value: 'nl',
  },
  {
    name: 'English',
    value: 'en',
  },
]);
const types = ref<Option<string>[]>([
  {
    name: 'Hours',
    value: 'hours',
  },
  {
    name: 'Other',
    value: 'items',
  },
]);

const submit = async () => {
  $v.value.$touch();
  if (!$v.value.$invalid) {
    setLoading(true);
    const res: DataResponse<Invoice> = await store.dispatch(
      BalanceActions.UpdateInvoice,
      invoice,
    );

    if (res.status) {
      await store.dispatch(BalanceActions.GetInvoice, {
        number: props.data.invoice.number,
      });
      cancel();
    }

    setLoading(false);
  }
};

const cancel = () => {
  emit('close');
};

const removeLine = (line: InvoiceLine) => {
  invoice.removeLine(line);
};

const addLine = () => {
  invoice.addLine();
};

const removeOption = (option: InvoiceOption) => {
  invoice.removeOption(option);
};

const addOption = () => {
  invoice.addOption();
};

const updateLanguage = (l: 'en' | 'nl') => {
  language.value = l;
  invoice.language = l;
  invoice.subject = `${getMonthName(date.getMonth(), l)} ${date.getFullYear()}`;
};
</script>
<template>
  <section class="drawer-content invoice-form">
    <form
      class="form invoice-form"
      @submit.prevent="submit"
      :class="{ submitting: loading }"
    >
      <h2>Update Invoice</h2>
      <div class="invoice" v-if="data.info && invoice">
        <ul class="items invoice-info">
          <li>
            <label>Invoice number</label>
            <div>{{ invoice.invoiceNumber }}</div>
          </li>
          <li>
            <label>Invoice date</label>
            <div>{{ data.invoice.date_created_f }}</div>
          </li>
          <li>
            <label>Invoice period</label>
            <div>{{ invoice.subject }}</div>
          </li>
        </ul>

        <div class="columns">
          <Field
            label="Invoice period"
            placeholder="Invoice period"
            styling="col two-quarters"
            v-model="invoice.subject"
            :validation="$v.subject"
          ></Field>
        </div>
        <FieldOptions
          label="Language"
          @update:modelValue="updateLanguage"
          styling="col two-quarters"
          type="radio"
          :options="languages"
          v-model="invoice.language"
        ></FieldOptions>
        <FieldOptions
          label="Type"
          styling="col two-quarters"
          type="radio"
          :options="types"
          v-model="invoice.type"
        ></FieldOptions>

        <p>
          Enter the VAT and the amount of the invoice. Add more lines as needed.
        </p>
        <InvoiceFormLine
          v-for="line in invoice.lines"
          :line="line"
          :key="line.name"
          :isOnly="invoice.lines.length === 1"
          @remove="removeLine(line)"
          @add="addLine()"
        ></InvoiceFormLine>
        <div class="balance-total-line columns">
          <div class="col third" />
          <div class="col onetenth" />
          <div class="col onefifth" />
          <div class="col oneeight field">
            <label>Subtotal</label>
          </div>
          <FieldNumber
            label="Subtotal"
            hide-label
            readonly
            styling="col oneeight"
            v-model="invoice.sub_total"
            step="0.01"
          ></FieldNumber>
          <div class="col onetenth" />
        </div>
        <div class="balance-total-line columns">
          <div class="col third" />
          <div class="col onetenth" />
          <div class="col onetenth" />
          <div class="col onetenth field">
            <label>VAT</label>
          </div>
          <FieldNumber
            label="VatTotal"
            hide-label
            readonly
            styling="col oneeight"
            v-model="invoice.vat_total"
            step="0.01"
          ></FieldNumber>
          <div class="col onetenth" />
        </div>
        <div class="balance-total-line columns">
          <div class="col third" />
          <div class="col onetenth" />
          <div class="col onefifth" />
          <div class="col oneeight field">
            <label>Total</label>
          </div>
          <FieldNumber
            label="total"
            hide-label
            readonly
            styling="col oneeight"
            v-model="invoice.total"
            step="0.01"
          ></FieldNumber>
          <div class="col onetenth" />
        </div>
        <div class="buttons">
          <button class="confirm" type="submit" :disabled="loading">
            <span class="icon icon-pencil"></span>
            <span class="text">Update</span>
          </button>
          <button class="cancel" type="button" @click="cancel">
            <span class="icon icon-remove"></span>
            <span class="text">Clear</span>
          </button>
          <Loading v-if="loading"></Loading>
        </div>
      </div>
    </form>
  </section>
</template>
